import React, { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import '../../styles/global.css';
import styles from './subscribed.module.css';
import DownloadApp from '../../components/DownloadApp';
import studioBottomIcon from '../../assets/images/verticaltv/studio-bottom-icon.png';
import crown from '../../assets/images/verticaltv/crown.png';
import { CLEVERTAP_EVENTS } from '../../analytics/trackerEvents/cleverTap/events';
import { CT_PARAMS } from '../../analytics/trackerEvents/cleverTap/parameters';
import { trackCleverTapEvent } from '../../utils/cleverTapHelper';
import { useTokenStore } from '../../storage/tokenState';
import { getVerticalTVRedirectUrl, getUTMParamsFromURL } from '../../utils/utmTracker';
import appsFlyerSmartScriptLib from '../../utils/SmartScriptAppFlyer/appsFlyerSmartScriptLib';
import { getStoredOneLinkURL, fireButtonClickEvent } from '../../utils/SmartScriptAppFlyer/appsFlyerEvents';
import { useSubscriptionPaymentStore } from '../../storage/subscriptionPaymentStore';


// Check if user has real UTM params (not just defaults)
const hasRealUTMParams = (): boolean => {
  const utmParams = getUTMParamsFromURL();
  // Check if any UTM param exists and is not empty
  return !!(utmParams.utm_source || utmParams.utm_campaign || utmParams.fbclid || utmParams.gclid);
};

// Default Play Store URL (fallback)
const DEFAULT_PLAYSTORE_URL = 'https://play.google.com/store/apps/details?id=com.eight.network.android.vertical';

const Subscribed: React.FC = () => {
  const { token } = useTokenStore();
  const location = useLocation();
  const [appsFlyerDownloadUrl, setAppsFlyerDownloadUrl] = useState<string | null>(null);
  const smartScriptInitializedRef = useRef(false);

  const resetSubscriptionPaymentDataCtx = useSubscriptionPaymentStore((state)=>state.resetSubscriptionPaymentDataCtx);

  useEffect(()=>{
    resetSubscriptionPaymentDataCtx();
  },[]);

  // Check if user came from payment-processing flow
  const fromPaymentProcessing = location.state?.fromPaymentProcessing || false;
  
  // Check if user came with titleId - use deep link ONLY when titleId is present
  const verticalTVUrl = getVerticalTVRedirectUrl();
  
  // Check if we have real UTM params (cached on mount)
  const hasUTMParams = useRef(hasRealUTMParams());
  

  const getDownloadUrl = (): string => {
    // Priority 1: Deep link when titleId is in params
    if (verticalTVUrl) {
      return verticalTVUrl;
    }

    if (appsFlyerDownloadUrl && hasUTMParams.current) {
      return appsFlyerDownloadUrl;
    }
    
    // Priority 3: Default Play Store URL for direct visits (no marketing attribution)
    return DEFAULT_PLAYSTORE_URL;
  };

  // Clear any leftover payment-related sessionStorage when user is on subscribed page
  // This prevents redirect to payment screen when user returns from Play Store
  useEffect(() => {
    sessionStorage.removeItem('pendingOrderId');
    sessionStorage.removeItem('paymentInitiated');
    sessionStorage.removeItem('paymentTimestamp');
    sessionStorage.removeItem('pendingPaymentAmount');
    sessionStorage.removeItem('mandateUri');
    sessionStorage.removeItem('mandateUriOpened');
    sessionStorage.removeItem('shouldNavigateToProcessing');
    
    // Prevent WebOTP popup on this page by removing OTP-related inputs
    const otpInputs = document.querySelectorAll('input[autocomplete="one-time-code"]');
    otpInputs.forEach((input) => {
      input.removeAttribute('autocomplete');
    });
    
    // Note: Facebook Pixel events (FbPixelEvent_StartTrial, FbPixelEvent_Subscribe) 
    // are fired in PaymentProcessing page when subscription is detected as active
  }, []);

  // Initialize AppsFlyer SmartScript and get OneLink URL for download button
  useEffect(() => {
    // Prevent duplicate initialization (handles React.StrictMode double-mounting)
    if (smartScriptInitializedRef.current) {
      return;
    }
    smartScriptInitializedRef.current = true;
    
    try {
      // Initialize SmartScript (captures UTM params from URL)
      appsFlyerSmartScriptLib();
      
      // Get the generated OneLink URL after SmartScript is initialized
      // Use setTimeout to ensure SmartScript has processed the URL
      setTimeout(() => {
        const oneLinkUrl = getStoredOneLinkURL();
        if (oneLinkUrl) {
          setAppsFlyerDownloadUrl(oneLinkUrl);
        }
      }, 100);
    } catch (error) {
      // Silent fail - fallback to default Play Store URL
    }
  }, []);

  const handleDownloadClick = () => {
    const userId = token?.zupeeUserId?.toString() || '';
    
    // Track CleverTap event
    trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_DOWNLOAD_APP_CLICKED, {
      [CT_PARAMS.USER_ID]: userId
    });
    
    // Track AppsFlyer button click event
    fireButtonClickEvent({
      buttonName: 'download_app',
      page: 'subscribed',
      action: 'download'
    });
  };
  return (
    <div className={styles.subscribedPage}>
      <div className={styles.backgroundGlow}>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="360" 
          height="621" 
          viewBox="0 0 360 621" 
          fill="none"
          className={styles.greenCornerSvg}
        >
          <g filter="url(#filter0_fn_531_2611)">
            <circle cx="107" cy="592" r="158" fill="#0C4615"/>
          </g>
          <defs>
            <filter id="filter0_fn_531_2611" x="-485" y="0" width="1184" height="1184" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
              <feGaussianBlur stdDeviation="217" result="effect1_foregroundBlur_531_2611"/>
              <feTurbulence type="fractalNoise" baseFrequency="0.5 0.5" stitchTiles="stitch" numOctaves="3" result="noise" seed="1097"/>
              <feComponentTransfer in="noise" result="coloredNoise1">
                <feFuncR type="linear" slope="2" intercept="-0.5"/>
                <feFuncG type="linear" slope="2" intercept="-0.5"/>
                <feFuncB type="linear" slope="2" intercept="-0.5"/>
                <feFuncA type="discrete" tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "/>
              </feComponentTransfer>
              <feComposite operator="in" in2="effect1_foregroundBlur_531_2611" in="coloredNoise1" result="noise1Clipped"/>
              <feComponentTransfer in="noise1Clipped" result="color1">
                <feFuncA type="table" tableValues="0 0.3"/>
              </feComponentTransfer>
              <feMerge result="effect2_noise_531_2611">
                <feMergeNode in="effect1_foregroundBlur_531_2611"/>
                <feMergeNode in="color1"/>
              </feMerge>
            </filter>
          </defs>
        </svg>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="360" 
          height="684" 
          viewBox="0 0 360 684" 
          fill="none"
          className={styles.backgroundSvg}
        >
          <g filter="url(#filter0_fn_531_2610)">
            <circle cx="360" cy="92" r="158" fill="#2CB642"/>
          </g>
          <defs>
            <filter 
              id="filter0_fn_531_2610" 
              x="-232" 
              y="-500" 
              width="1184" 
              height="1184" 
              filterUnits="userSpaceOnUse" 
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
              <feGaussianBlur stdDeviation="217" result="effect1_foregroundBlur_531_2610"/>
              <feTurbulence 
                type="fractalNoise" 
                baseFrequency="0.5 0.5" 
                stitchTiles="stitch" 
                numOctaves="3" 
                result="noise" 
                seed="2135"
              />
              <feComponentTransfer in="noise" result="coloredNoise1">
                <feFuncR type="linear" slope="2" intercept="-0.5"/>
                <feFuncG type="linear" slope="2" intercept="-0.5"/>
                <feFuncB type="linear" slope="2" intercept="-0.5"/>
                <feFuncA 
                  type="discrete" 
                  tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0"
                />
              </feComponentTransfer>
              <feComposite 
                operator="in" 
                in2="effect1_foregroundBlur_531_2610" 
                in="coloredNoise1" 
                result="noise1Clipped"
              />
              <feComponentTransfer in="noise1Clipped" result="color1">
                <feFuncA type="table" tableValues="0 0.4"/>
              </feComponentTransfer>
              <feMerge result="effect2_noise_531_2610">
                <feMergeNode in="effect1_foregroundBlur_531_2610"/>
                <feMergeNode in="color1"/>
              </feMerge>
            </filter>
          </defs>
        </svg>
      </div>
      <div className={styles.contentContainer}>
        <div className={styles.iconContainer}>
          <div className={styles.shadowWrapper}>
            <svg 
              className={styles.iconShadow}
              xmlns="http://www.w3.org/2000/svg" 
              width="325" 
              height="264" 
              viewBox="0 0 325 264" 
              fill="none"
            >
              <g filter="url(#filter0_f_531_2607)">
                <path 
                  d="M34 34H291L248.682 199.887C244.162 217.603 228.206 230 209.923 230H115.077C96.7937 230 80.8376 217.603 76.3182 199.887L34 34Z" 
                  fill="url(#paint0_linear_531_2607)"
                />
              </g>
              <defs>
                <filter 
                  id="filter0_f_531_2607" 
                  x="0" 
                  y="0" 
                  width="325" 
                  height="264" 
                  filterUnits="userSpaceOnUse" 
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                  <feGaussianBlur stdDeviation="17" result="effect1_foregroundBlur_531_2607"/>
                </filter>
                <linearGradient 
                  id="paint0_linear_531_2607" 
                  x1="163" 
                  y1="220" 
                  x2="163" 
                  y2="34" 
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#063947"/>
                  <stop offset="1" stopColor="#513606" stopOpacity="0"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          <div className={styles.smileyIcon}>
            <img 
              src={studioBottomIcon} 
              alt="Studio Icon" 
              className={styles.smileyImage}
            />
            <img 
              src={crown} 
              alt="Crown" 
              className={styles.crownImage}
            />
          </div>
        </div>
        
        <div className={styles.textContainer}>
          <h1 className={styles.mainHeading}>
            {fromPaymentProcessing ? (
              <>
                <span>Subscription</span>
                <span>activated</span>
              </>
            ) : (
              <>
                <span>You're already</span>
                <span>subscribed</span>
              </>
            )}
          </h1>
        </div>
        
        <div className={styles.downloadSection}>
          <div className={styles.buttonGroup}>
            <p className={styles.subHeading}>
              {verticalTVUrl ? 'Open app and enjoy your premium benefits' : 'Download app and enjoy your premium benefits'}
            </p>
            <div className={styles.buttonWrapper}>
              {/* Button only - always pass a valid URL */}
              <DownloadApp 
                showRedirecting={false} 
                showButton={true}
                redirectUrl={getDownloadUrl()}
                onClick={handleDownloadClick}
              />
            </div>
          </div>
          <div className={styles.redirectingWrapper}>
            {/* Auto-redirect countdown only */}
            <DownloadApp 
              showRedirecting={true} 
              showButton={false}
              redirectUrl={getDownloadUrl()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscribed;

